﻿import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Modal, Input } from '../../../components/ui';
import { fetchAPI } from '../../../services/apiClient';
import {
  ShoppingCart,
  CreditCard,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Plus,
  Trash2,
  Lock,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Loader2,
  Clock,
  Banknote,
  Smartphone,
} from 'lucide-react';
import { useAuth } from '../../../app/auth';
import {
  getResumenSistema,
  getResumenOperativo,
  getEstadoCierre,
  cerrarCajaVendedor,
  getCierresHistoricos,
  getMovimientosResumen,
  getDetalleVentasFecha,
  abrirCaja,
  devolucionLoteria,
  getAperturaInfo,
  type AperturaInfo,
  type InventarioItem,
  type ResumenSistema,
  type CajaResumenOperativo,
  type CajaMovimiento,
  type MovimientoResumen,
  type EstadoCierre,
  type ValeInput,
  type LoteriaFisicaInput,
  type LoteriaAsignada,
  type CierreHistorico,
    type IngresoExtraInput,
} from '../services/cajaVendedorService';
import { fechaLocalYMD } from '../../../utils/date';
import { formatCRC } from '../../../utils/format';

// ========================
// Helpers
// ========================

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return formatCRC(Number.isFinite(num) ? num : 0);
}

function today(): string {
  return fechaLocalYMD();
}

// ========================
// Mapa de etiquetas por tipo de movimiento
// ========================

const TIPO_DISPLAY: Record<string, string> = {
  VENTAS_RESUMEN: 'Resumen ventas',
  VENTA: 'Venta',
  VENTA_CREDITO: 'Venta crédito',
  ANULACION_VENTA: 'Anulación',
  ANULACION_VENTA_CREDITO: 'Anulación crédito',
  PREMIO_PAGADO: 'Premio pagado',
  INGRESO_EXTRA: 'Ingreso extra',
  EGRESO_MANUAL: 'Egreso manual',
  VALE: 'Vale',
  AJUSTE_INGRESO: 'Ajuste ingreso',
  AJUSTE_EGRESO: 'Ajuste egreso',
  ABONO_CREDITO: 'Abono crédito (ef.)',
  ABONO_CREDITO_SINPE: 'Abono crédito (SINPE)',
  ABONO_CREDITO_TRANSFERENCIA: 'Abono crédito (transf.)',
  ABONO_CREDITO_LOCAL: 'Abono crédito (local)',
  COMPRA_RECARGA: 'Compra recarga',
};

function getLabelTipo(tipo: string): string {
  return TIPO_DISPLAY[tipo] ?? tipo;
}

// Badge color por tipo
function getBadgeClass(tipo: string, naturaleza: string): string {
  if (tipo === 'VENTAS_RESUMEN') return 'bg-blue-100 text-blue-700';
  if (tipo.startsWith('ANULACION')) return 'bg-yellow-100 text-yellow-700';
  if (tipo === 'PREMIO_PAGADO') return 'bg-orange-100 text-orange-700';
  if (tipo === 'VALE' || tipo === 'EGRESO_MANUAL') return 'bg-red-100 text-red-700';
  if (tipo.startsWith('ABONO_CREDITO')) return 'bg-purple-100 text-purple-700';
  if (naturaleza === 'INGRESO') return 'bg-emerald-100 text-emerald-700';
  return 'bg-red-100 text-red-700';
}

// ========================
// MovimientosCajaTable
// ========================

interface MovimientosCajaTableProps {
  movimientos: import('../services/cajaVendedorService').MovimientoResumen[];
  fecha: string;
  onVerDetalle: (fecha: string) => void;
}

function MovimientosCajaTable({ movimientos, fecha, onVerDetalle }: MovimientosCajaTableProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-base font-bold text-gray-900">Movimientos de caja</h2>
        <span className="text-xs text-gray-500">Hasta {fecha}</span>
      </div>
      <div className="overflow-x-auto">
        {movimientos.length === 0 ? (
          <p className="px-5 py-6 text-sm text-gray-500">No hay movimientos registrados para este corte.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Fecha</th>
                <th className="px-4 py-3 text-left font-semibold">Tipo</th>
                <th className="px-4 py-3 text-left font-semibold">Descripción</th>
                <th className="px-4 py-3 text-right font-semibold">Monto</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {movimientos.map((mov, idx) => (
                <tr key={mov.id ?? `${mov.fecha_operativa}-${idx}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{mov.fecha_operativa}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${getBadgeClass(mov.tipo, mov.naturaleza)}`}>
                      {getLabelTipo(mov.tipo)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{mov.descripcion}</td>
                  <td className={`px-4 py-3 text-right font-mono font-semibold whitespace-nowrap ${mov.naturaleza === 'INGRESO' ? 'text-emerald-700' : 'text-red-700'}`}>
                    {mov.naturaleza === 'INGRESO' ? '+' : '-'}{formatCurrency(mov.monto)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {mov.tipo === 'VENTAS_RESUMEN' && (
                      <button
                        onClick={() => onVerDetalle(mov.fecha_operativa)}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium underline underline-offset-2 whitespace-nowrap"
                      >
                        Ver detalle
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ========================
// Component
// ========================

export const CajaVendedorPage: React.FC = () => {
  const { user } = useAuth();
  const [fecha, setFecha] = useState(today());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [abriendoCaja, setAbriendoCaja] = useState(false);
  const [saldoInicialInput, setSaldoInicialInput] = useState('');
  // Continuidad: control de aperturas (Â§2.3 AGENTS.md)
  const [showAdvertenciaApertura, setShowAdvertenciaApertura] = useState(false);
  const [advertenciaMsg, setAdvertenciaMsg] = useState('');
  const [pendienteConfirmarApertura, setPendienteConfirmarApertura] = useState(false);

  // Data from system
  const [resumen, setResumen] = useState<ResumenSistema | null>(null);
  const [resumenOperativo, setResumenOperativo] = useState<CajaResumenOperativo | null>(null);
  const [movimientosResumen, setMovimientosResumen] = useState<MovimientoResumen[]>([]);
  const [showDetalleVentas, setShowDetalleVentas] = useState(false);
  const [detalleVentasFecha, setDetalleVentasFecha] = useState<string | null>(null);
  const [detalleVentasItems, setDetalleVentasItems] = useState<CajaMovimiento[]>([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [estado, setEstado] = useState<EstadoCierre | null>(null);
  const [historicos, setHistoricos] = useState<CierreHistorico[]>([]);
  const [showHistoricos, setShowHistoricos] = useState(false);
  const [movimientoManual, setMovimientoManual] = useState({
    tipo: 'INGRESO_EXTRA',
    monto: '',
    descripcion: '',
  });
  const [guardandoMovimientoManual, setGuardandoMovimientoManual] = useState(false);

  // Manual fields

  // Bloque C - Vales
  const [vales, setVales] = useState<ValeInput[]>([]);

    // Bloque F - Ingresos extra
    const [ingresosExtra, setIngresosExtra] = useState<IngresoExtraInput[]>([]);

  // Bloque G - LoterÃ­a fÃ­sica (auto desde inventario)
  const [loteriasAsignadas, setLoteriasAsignadas] = useState<LoteriaAsignada[]>([]);
  const [loteriaCantidadFinal, setLoteriaCantidadFinal] = useState<Record<number, number>>({});
  const [devJuegoId, setDevJuegoId] = useState<number | null>(null);
  const [devCantidad, setDevCantidad] = useState('');

  // Efectivo contado
  const [efectivoContado, setEfectivoContado] = useState('');
  const [notas, setNotas] = useState('');

  // Confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Success tracking (para mostrar botÃ³n de imprimir despuÃ©s de cerrar)
  const [ultimoCierreId, setUltimoCierreId] = useState<string | null>(null);

  // Expanded bloque states
  const [expandedBloques, setExpandedBloques] = useState<Record<string, boolean>>({
     A: true, C: true, D: true, E: true, F: true, G: true,
  });

  // Apertura: informaciÃ³n del Ãºltimo cierre y tab activa
  const [aperturaInfo, setAperturaInfo] = useState<AperturaInfo | null>(null);
  const [inventarioInicialRows, setInventarioInicialRows] = useState<{ nombre: string; cantidad: string }[]>([]);
  const [activeTab, setActiveTab] = useState<'operaciones' | 'cierre'>('operaciones');

  // ========================
  // Load data
  // ========================

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [resumenData, resumenOperativoData, movimientosData, estadoData, historicosData] = await Promise.all([
        getResumenSistema(fecha),
        getResumenOperativo(undefined, fecha),
        getMovimientosResumen(undefined, fecha),
        getEstadoCierre(fecha),
        getCierresHistoricos(10),
      ]);
      setResumen(resumenData);
      setResumenOperativo(resumenOperativoData);
      setMovimientosResumen(movimientosData);
      setEstado(estadoData);
      setHistoricos(historicosData);

      // Cargar informaciÃ³n del Ãºltimo cierre para la pantalla de apertura
      try {
        const aperInfo = await getAperturaInfo();
        setAperturaInfo(aperInfo);
        // Pre-llenar saldo e inventario si hay sugerencia y caja aÃºn no estÃ¡ abierta
        if (aperInfo.sugerencia && estadoData.caja_estado === 'NO_INICIADA') {
          setSaldoInicialInput(aperInfo.sugerencia.efectivo_inicial_sugerido || '');
          setInventarioInicialRows(
            aperInfo.sugerencia.inventario_inicial_sugerido.map((i: InventarioItem) => ({
              nombre: i.nombre,
              cantidad: String(i.cantidad),
            }))
          );
        }
      } catch {
        // No crÃ­tico si falla
        setAperturaInfo(null);
      }
      // Poblar loterÃ­as asignadas desde inventario
      if (resumenData.loterias_fisicas?.asignaciones) {
        setLoteriasAsignadas(resumenData.loterias_fisicas.asignaciones);
        // Inicializar cantidad_final en 0 para cada juego (si no existe ya)
        setLoteriaCantidadFinal(prev => {
          const next = { ...prev };
          for (const la of resumenData.loterias_fisicas.asignaciones) {
            if (!(la.juego_id in next)) {
              next[la.juego_id] = 0;
            }
          }
          return next;
        });
      } else {
        setLoteriasAsignadas([]);
      }
    } catch (err: any) {
      setError(err.message || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, [fecha]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ========================
  // Calculations
  // ========================

  const totalVales = useMemo(() => {
    return vales.reduce((sum, v) => sum + (parseFloat(v.monto) || 0), 0);
  }, [vales]);

    const totalIngresosExtra = useMemo(() => {
      return ingresosExtra.reduce((sum, ie) => sum + (parseFloat(ie.monto) || 0), 0);
    }, [ingresosExtra]);

  const totalLoteriaFisica = useMemo(() => {
    return loteriasAsignadas.reduce((sum, la) => {
      const cantFinal = loteriaCantidadFinal[la.juego_id] ?? 0;
      const vendidos = Math.max(la.neto - cantFinal, 0);
      return sum + vendidos * la.valor_unitario;
    }, 0);
  }, [loteriasAsignadas, loteriaCantidadFinal]);

  const totalVentasLoteria = parseFloat(resumen?.ventas.total_ventas_loteria || '0');
  const totalPremios = parseFloat(resumen?.premios.total_premios_pagados || '0');
  const totalCreditoVendido = parseFloat(resumen?.creditos.total_credito_vendido || '0');
  const totalCreditoCobrado = parseFloat(resumen?.creditos.total_credito_cobrado || '0');
  const totalComprasRecarga = parseFloat(resumen?.recargas?.total_compras_recarga || '0');

  // Pagos digitales (SINPE + TRANSFERENCIA) â€” ya incluidos en ventas, pero NO son efectivo fÃ­sico
  const totalSinpe = parseFloat(resumen?.pagos_digitales?.total_sinpe || '0');
  const totalTransferencia = parseFloat(resumen?.pagos_digitales?.total_transferencia || '0');
  const totalDigital = totalSinpe + totalTransferencia;

  // Ingresos = ventas_loteria + loteria_fisica + ingresos_extra + creditos_cobrados
  const totalIngresos = totalVentasLoteria + totalLoteriaFisica + totalIngresosExtra + totalCreditoCobrado;
  // Egresos = premios + vales + compras_recarga
  // âœ… Compras de recarga son EGRESO (dinero sale de la caja)
  const totalEgresos = totalPremios + totalVales + totalComprasRecarga;
  const saldoTeorico = totalIngresos - totalEgresos;

  // Saldo inicial de apertura (fondo con el que arrancÃ³ la caja)
  const saldoInicialSesion = parseFloat(estado?.saldo_inicial || '0');

  // Efectivo esperado = saldo_inicial + saldo_teÃ³rico âˆ’ pagos digitales
  // âœ… El fondo de apertura siempre forma parte del efectivo esperable al cierre
  const efectivoEsperado = saldoInicialSesion + saldoTeorico - totalDigital;

  const efectivoNum = parseFloat(efectivoContado) || 0;
  const diferencia = efectivoNum - efectivoEsperado;

  const estadoDiferencia = diferencia > 0 ? 'SOBRA' : diferencia < 0 ? 'FALTA' : 'CUADRA';

  // ========================
  // Validation
  // ========================

  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    for (let i = 0; i < vales.length; i++) {
      if (!vales[i].justificacion.trim()) {
        errors.push(`Vale #${i + 1}: falta justificación`);
      }
      if (parseFloat(vales[i].monto) <= 0 || !vales[i].monto) {
        errors.push(`Vale #${i + 1}: monto inválido`);
      }
    }
      for (let i = 0; i < ingresosExtra.length; i++) {
        if (!ingresosExtra[i].concepto.trim()) {
          errors.push(`Ingreso extra #${i + 1}: falta concepto`);
        }
        if (parseFloat(ingresosExtra[i].monto) <= 0 || !ingresosExtra[i].monto) {
          errors.push(`Ingreso extra #${i + 1}: monto inválido`);
        }
      }
    if (!efectivoContado || parseFloat(efectivoContado) < 0) {
      errors.push('Debe ingresar el efectivo contado');
    }
    return errors;
  }, [vales, efectivoContado]);

  const canClose = validationErrors.length === 0 && !estado?.cerrada;

  // ========================
  // Submit
  // ========================

  const handleCerrar = async () => {
    setShowConfirmModal(false);
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
      try {
      const result = await cerrarCajaVendedor({
        fecha,
        vales,
          ingresos_extra: ingresosExtra,
        loteria_fisica: loteriasAsignadas.map(la => ({
          juego_id: la.juego_id,
          cantidad_final: loteriaCantidadFinal[la.juego_id] ?? 0,
        })),
        efectivo_contado: efectivoContado || '0',
        notas: notas || undefined,
      });
      setSuccessMsg('Caja cerrada exitosamente');
        setUltimoCierreId(result.id);
      await loadData();
    } catch (err: any) {
      if (err.message?.includes('409') || err.message?.toLowerCase().includes('ya existe')) {
        setError('Esta caja ya fue cerrada para la fecha seleccionada.');
      } else {
        setError(err.message || 'Error al cerrar caja');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRegistrarMovimientoManual = async () => {
    const monto = parseFloat(movimientoManual.monto);
    const descripcion = movimientoManual.descripcion.trim();

    if (!movimientoManual.tipo) {
      setError('Debe seleccionar el tipo de movimiento manual.');
      return;
    }
    if (!descripcion) {
      setError('Debe indicar una descripción para el movimiento manual.');
      return;
    }
    if (!Number.isFinite(monto) || monto <= 0) {
      setError('Debe indicar un monto válido mayor a cero.');
      return;
    }

    setGuardandoMovimientoManual(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await fetchAPI('/caja-vendedor/movimientos/manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: movimientoManual.tipo,
          monto: movimientoManual.monto,
          descripcion,
          fecha,
        }),
      });
      setMovimientoManual({ tipo: 'INGRESO_EXTRA', monto: '', descripcion: '' });
      setSuccessMsg('Movimiento manual registrado correctamente.');
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Error al registrar movimiento manual');
    } finally {
      setGuardandoMovimientoManual(false);
    }
  };

  // ========================
  // Vale helpers
  // ========================

  const addVale = () => setVales([...vales, { monto: '', justificacion: '' }]);
  const removeVale = (i: number) => setVales(vales.filter((_, idx) => idx !== i));
  const updateVale = (i: number, field: keyof ValeInput, value: string) => {
    const updated = [...vales];
    updated[i] = { ...updated[i], [field]: value };
    setVales(updated);
  };

    // ========================
    // Ingreso extra helpers
    // ========================

    const addIngresoExtra = () => setIngresosExtra([...ingresosExtra, { concepto: '', monto: '' }]);
    const removeIngresoExtra = (i: number) => setIngresosExtra(ingresosExtra.filter((_, idx) => idx !== i));
    const updateIngresoExtra = (i: number, field: keyof IngresoExtraInput, value: string) => {
      const updated = [...ingresosExtra];
      updated[i] = { ...updated[i], [field]: value };
      setIngresosExtra(updated);
    };

  // ========================
  // Inventario inicial helpers (apertura)
  // ========================

  const addInventarioRow = () => setInventarioInicialRows([...inventarioInicialRows, { nombre: '', cantidad: '0' }]);
  const removeInventarioRow = (i: number) => setInventarioInicialRows(inventarioInicialRows.filter((_, idx) => idx !== i));
  const updateInventarioRow = (i: number, field: 'nombre' | 'cantidad', value: string) => {
    const updated = [...inventarioInicialRows];
    updated[i] = { ...updated[i], [field]: value };
    setInventarioInicialRows(updated);
  };

  // ========================
  // DevoluciÃ³n de loterÃ­a
  // ========================

  const handleDevolucion = async () => {
    if (!devJuegoId || !devCantidad) return;
    try {
      await devolucionLoteria(devJuegoId, parseInt(devCantidad));
      setDevJuegoId(null);
      setDevCantidad('');
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Error al registrar devoluciÃ³n');
    }
  };

  // ========================
  // Toggle bloque
  // ========================

  const toggleBloque = (key: string) => {
    setExpandedBloques(prev => ({ ...prev, [key]: !prev[key] }));
  };

    // ========================
    // Imprimir cierre
    // ========================

    const imprimirCierre = (cierreId: string) => {
      const url = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/caja-vendedor/cierre/${cierreId}/imprimir`;
      window.open(url, '_blank', 'width=900,height=800');
    };

  // ========================
  // Render
  // ========================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-600">Cargando caja del vendedor...</span>
      </div>
    );
  }

  const cajaCerrada = estado?.cerrada;
  const cajaEstadoSesion = estado?.caja_estado || 'NO_INICIADA';

  // Abrir caja handler
  const handleAbrirCaja = async () => {
    setAbriendoCaja(true);
    setError(null);
    try {
      const saldo = parseFloat(saldoInicialInput.replace(/,/g, '')) || 0;
      const inventario = inventarioInicialRows
        .filter(r => r.nombre.trim())
        .map(r => ({ nombre: r.nombre.trim(), cantidad: parseInt(r.cantidad) || 0 }));
      const resp = await abrirCaja(saldo, inventario);
      
      // Si hay advertencia (apertura 2 o 3), mostrar modal ANTES de proceder
      if (resp.advertencia && (resp.apertura_numero === 2 || resp.apertura_numero === 3)) {
        setAdvertenciaMsg(resp.advertencia);
        setShowAdvertenciaApertura(true);
        setPendienteConfirmarApertura(true);
        // NO limpiar aquÃ­: esperar confirmaciÃ³n del usuario
        setAbriendoCaja(false);
        return;
      }
      
      // Sin advertencia: proceder normalmente
      setSaldoInicialInput('');
      await loadData();
      setSuccessMsg('Caja abierta exitosamente');
    } catch (err: any) {
      const msgErr = err.message || 'Error al abrir caja';
      // Si es error 403 por exceso de aperturas:
      if (msgErr.includes('Límite de aperturas')) {
        setError(`${msgErr} Contacte a su supervisor.`);
      } else {
        setError(msgErr);
      }
    } finally {
      setAbriendoCaja(false);
    }
  };

  // Confirmar apertura despuÃ©s de advertencia
  const handleConfirmarAperturaAdvertida = async () => {
    setShowAdvertenciaApertura(false);
    setPendienteConfirmarApertura(false);
    setAbriendoCaja(true);
    setError(null);
    try {
      const saldo = parseFloat(saldoInicialInput.replace(/,/g, '')) || 0;
      const inventario = inventarioInicialRows
        .filter(r => r.nombre.trim())
        .map(r => ({ nombre: r.nombre.trim(), cantidad: parseInt(r.cantidad) || 0 }));
      await abrirCaja(saldo, inventario);
      setSaldoInicialInput('');
      await loadData();
      setSuccessMsg('Caja abierta exitosamente');
    } catch (err: any) {
      setError(err.message || 'Error al abrir caja');
    } finally {
      setAbriendoCaja(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-24">

      {/* â”€â”€â”€ Header â”€â”€â”€ */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Caja del Vendedor</h1>
          <p className="text-sm text-gray-500">
            {resumen?.vendedor_nombre || user?.nombre || user?.username} - Corte diario
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="w-40" />
          <Button variant="secondary" onClick={loadData} className="!p-2">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* â”€â”€â”€ Mensajes globales â”€â”€â”€ */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2">
          <XCircle className="w-5 h-5 text-red-600 shrink-0" />
          <span className="text-red-700 flex-1">{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 text-lg leading-none">x</button>
        </div>
      )}
      {successMsg && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700">{successMsg}</span>
          </div>
          {ultimoCierreId && (
            <button
              onClick={() => imprimirCierre(ultimoCierreId)}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg flex items-center gap-1 shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Imprimir
            </button>
          )}
        </div>
      )}

      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      {/* PANTALLA APERTURA â€” estado NO_INICIADA                   */}
      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      {cajaEstadoSesion === 'NO_INICIADA' && !cajaCerrada && (
        <div className="space-y-4">
          {/* Banner último cierre */}
          {aperturaInfo?.tiene_historial && aperturaInfo.ultimo_cierre && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h3 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Ultimo cierre registrado ({aperturaInfo.ultimo_cierre.fecha})
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white rounded-lg p-3 border border-blue-100 text-center">
                  <p className="text-xs text-blue-500 mb-0.5">Ingresos</p>
                  <p className="font-bold font-mono text-blue-800 text-right">{formatCurrency(aperturaInfo.ultimo_cierre.total_ingresos)}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-100 text-center">
                  <p className="text-xs text-blue-500 mb-0.5">Egresos</p>
                  <p className="font-bold font-mono text-blue-800 text-right">{formatCurrency(aperturaInfo.ultimo_cierre.total_egresos)}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-100 text-center">
                  <p className="text-xs text-blue-500 mb-0.5">Efectivo contado</p>
                  <p className="font-bold font-mono text-blue-800 text-right">{formatCurrency(aperturaInfo.ultimo_cierre.efectivo_contado)}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-100 text-center">
                  <p className="text-xs text-blue-500 mb-0.5">Estado</p>
                  <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-semibold ${
                    aperturaInfo.ultimo_cierre.estado_diferencia === 'CUADRA' ? 'bg-green-100 text-green-700' :
                    aperturaInfo.ultimo_cierre.estado_diferencia === 'SOBRA' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>{aperturaInfo.ultimo_cierre.estado_diferencia}</span>
                </div>
              </div>
              {aperturaInfo.ultimo_cierre.inventario_final.length > 0 && (
                <p className="mt-3 text-xs text-blue-600">
                  <span className="font-medium">Inventario del cierre anterior: </span>
                  {aperturaInfo.ultimo_cierre.inventario_final.map(i => `${i.nombre}: ${i.cantidad_final}`).join(' · ')}
                </p>
              )}
            </div>
          )}

          {/* Formulario apertura */}
          <div className="bg-white border-2 border-yellow-300 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center shrink-0">
                <Banknote className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Apertura de Caja</h2>
                <p className="text-sm text-gray-500">Declare el efectivo y el inventario con el que inicia el día</p>
              </div>
              {estado && (
                <span className="ml-auto px-3 py-1 bg-yellow-100 rounded-full text-yellow-800 text-xs font-semibold shrink-0">
                  Aperturas: {estado.total_aperturas_hoy}/{estado.total_aperturas_permitidas}
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Efectivo inicial */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Efectivo inicial en caja (CRC)</label>
                {aperturaInfo?.sugerencia?.efectivo_inicial_sugerido && (
                  <p className="text-xs text-blue-500 mb-1">
                    Sugerido desde cierre anterior: {formatCurrency(aperturaInfo.sugerencia.efectivo_inicial_sugerido)}
                  </p>
                )}
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={saldoInicialInput}
                  onChange={(e) => setSaldoInicialInput(e.target.value)}
                  placeholder="0"
                  className="w-full border-2 border-gray-300 rounded-xl px-4 h-14 text-right font-mono text-xl font-bold focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
                />
              </div>

              {/* Inventario físico */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Inventario físico (opcional)</label>
                <p className="text-xs text-gray-400 mb-2">Loterías, raspas u otros artículos con los que inicia</p>
                <div className="space-y-2">
                  {inventarioInicialRows.map((row, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        type="text"
                        value={row.nombre}
                        onChange={(e) => updateInventarioRow(i, 'nombre', e.target.value)}
                        placeholder="Nombre (ej: Raspa 500)"
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-300"
                      />
                      <input
                        type="number"
                        min="0"
                        value={row.cantidad}
                        onChange={(e) => updateInventarioRow(i, 'cantidad', e.target.value)}
                        placeholder="Cant."
                        className="w-20 border border-gray-300 rounded-lg px-3 py-2 text-sm text-right focus:ring-2 focus:ring-yellow-300"
                      />
                      <button onClick={() => removeInventarioRow(i)} className="text-red-400 hover:text-red-600 p-2">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button onClick={addInventarioRow} className="flex items-center gap-1 text-sm text-yellow-600 hover:text-yellow-800 font-medium">
                    <Plus className="w-4 h-4" /> Agregar artículo
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={handleAbrirCaja}
                disabled={abriendoCaja || !estado?.puede_abrir_mas}
                className={`w-full h-14 text-lg font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors ${
                  !estado?.puede_abrir_mas
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-yellow-500 hover:bg-yellow-600 text-white shadow-md hover:shadow-lg disabled:bg-gray-400'
                }`}
              >
                {abriendoCaja
                  ? <><Loader2 className="w-5 h-5 animate-spin" /> Abriendo...</>
                  : <><Banknote className="w-5 h-5" /> Abrir Caja</>
                }
              </button>
            </div>
          </div>

          {/* HistÃ³rico de cierres en pantalla de apertura */}
          {historicos.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <button onClick={() => setShowHistoricos(!showHistoricos)} className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Cierres anteriores
                </h3>
                {showHistoricos ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {showHistoricos && (
                <div className="px-5 pb-4 border-t border-gray-100 pt-3 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="py-2 px-2 text-left">Fecha</th>
                        <th className="py-2 px-2 text-right">Ingresos</th>
                        <th className="py-2 px-2 text-right">Egresos</th>
                        <th className="py-2 px-2 text-right">Efectivo</th>
                        <th className="py-2 px-2 text-right">Dif.</th>
                        <th className="py-2 px-2 text-center">Estado</th>
                        <th className="py-2 px-2 text-center">Accion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historicos.map((h) => (
                        <tr key={h.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-1.5 px-2">{h.fecha}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{formatCurrency(h.total_ingresos)}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{formatCurrency(h.total_egresos)}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{formatCurrency(h.efectivo_contado)}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{formatCurrency(h.diferencia)}</td>
                          <td className="py-1.5 px-2 text-center">
                            <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-semibold ${
                              h.estado_diferencia === 'CUADRA' ? 'bg-green-100 text-green-700' :
                              h.estado_diferencia === 'SOBRA' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>{h.estado_diferencia}</span>
                          </td>
                          <td className="py-1.5 px-2 text-center">
                            <button onClick={() => imprimirCierre(h.id)} className="text-blue-600 hover:text-blue-800 text-xs underline">Imprimir</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      {/* CAJA CERRADA â€” Banner + HistÃ³rico                        */}
      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      {cajaCerrada && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-green-600" />
              <span className="text-green-700 font-bold text-lg">Caja cerrada para esta fecha</span>
            </div>
            {ultimoCierreId && (
              <button
                onClick={() => imprimirCierre(ultimoCierreId)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg flex items-center gap-1 shrink-0"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Imprimir cierre
              </button>
            )}
          </div>

          {historicos.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <button onClick={() => setShowHistoricos(!showHistoricos)} className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Cierres anteriores
                </h3>
                {showHistoricos ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {showHistoricos && (
                <div className="px-5 pb-4 border-t border-gray-100 pt-3 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="py-2 px-2 text-left">Fecha</th>
                        <th className="py-2 px-2 text-right">Ingresos</th>
                        <th className="py-2 px-2 text-right">Egresos</th>
                        <th className="py-2 px-2 text-right">Efectivo</th>
                        <th className="py-2 px-2 text-right">Dif.</th>
                        <th className="py-2 px-2 text-center">Estado</th>
                        <th className="py-2 px-2 text-center">Accion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historicos.map((h) => (
                        <tr key={h.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-1.5 px-2">{h.fecha}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{formatCurrency(h.total_ingresos)}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{formatCurrency(h.total_egresos)}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{formatCurrency(h.efectivo_contado)}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{formatCurrency(h.diferencia)}</td>
                          <td className="py-1.5 px-2 text-center">
                            <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-semibold ${
                              h.estado_diferencia === 'CUADRA' ? 'bg-green-100 text-green-700' :
                              h.estado_diferencia === 'SOBRA' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>{h.estado_diferencia}</span>
                          </td>
                          <td className="py-1.5 px-2 text-center">
                            <button onClick={() => imprimirCierre(h.id)} className="text-blue-600 hover:text-blue-800 text-xs underline">Imprimir</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      {/* CAJA ABIERTA â€” Tabs: Operaciones | Cierre               */}
      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      {cajaEstadoSesion === 'ABIERTA' && !cajaCerrada && (
        <div className="space-y-4">

          {/* Badge estado abierta */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-blue-600" />
            <span className="text-blue-700 font-medium">Caja abierta</span>
            {estado && (
              <span className="ml-2 px-2 py-0.5 bg-blue-100 rounded text-blue-800 text-xs font-semibold">
                Apertura {estado.apertura_numero}/{estado.total_aperturas_permitidas}
              </span>
            )}
            {estado?.saldo_inicial && parseFloat(estado.saldo_inicial) > 0 && (
              <span className="ml-auto text-blue-600 text-sm font-medium">
                Fondo: {formatCurrency(estado.saldo_inicial)}
              </span>
            )}
          </div>

          {/* NavegaciÃ³n de tabs */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-full sm:w-auto sm:inline-flex">
            <button
              onClick={() => setActiveTab('operaciones')}
              className={`flex-1 sm:flex-none px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                activeTab === 'operaciones' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Operaciones
            </button>
            <button
              onClick={() => setActiveTab('cierre')}
              className={`flex-1 sm:flex-none px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                activeTab === 'cierre' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Cierre de caja
            </button>
          </div>

          {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ Tab OPERACIONES â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          {activeTab === 'operaciones' && (
            <div className="space-y-4">

              {/* Summary cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Fondo inicial</p>
                  <p className="text-2xl font-bold font-mono text-gray-700 text-right">{formatCurrency(estado?.saldo_inicial || '0')}</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 shadow-sm">
                  <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">Ingresos acumulados</p>
                  <p className="text-2xl font-bold font-mono text-emerald-700 text-right">{formatCurrency(resumenOperativo?.total_ingresos || 0)}</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 shadow-sm">
                  <p className="text-xs font-medium text-red-600 uppercase tracking-wide mb-1">Egresos acumulados</p>
                  <p className="text-2xl font-bold font-mono text-red-700 text-right">{formatCurrency(resumenOperativo?.total_egresos || 0)}</p>
                </div>
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 shadow-sm">
                  <p className="text-xs font-medium text-slate-300 uppercase tracking-wide mb-1">Saldo operativo</p>
                  <p className="text-2xl font-bold font-mono text-white text-right">{formatCurrency(resumenOperativo?.saldo_actual || 0)}</p>
                </div>
              </div>

              {/* Movimiento manual */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-200">
                  <h2 className="text-base font-bold text-gray-900">Movimiento manual</h2>
                  <p className="text-xs text-gray-500">Registra ingresos o egresos operativos sobre la caja abierta.</p>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-[220px_180px_minmax(0,1fr)_160px] gap-3 items-end">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Tipo</label>
                      <select
                        value={movimientoManual.tipo}
                        onChange={(e) => setMovimientoManual((prev) => ({ ...prev, tipo: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="INGRESO_EXTRA">Ingreso extra</option>
                        <option value="ABONO_CREDITO">Abono crédito (ef.)</option>
                        <option value="EGRESO_MANUAL">Egreso manual</option>
                        <option value="RETIRO">Retiro de caja</option>
                        <option value="VALE">Vale</option>
                        <option value="AJUSTE_INGRESO">Ajuste ingreso</option>
                        <option value="AJUSTE_EGRESO">Ajuste egreso</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Monto</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={movimientoManual.monto}
                        onChange={(e) => setMovimientoManual((prev) => ({ ...prev, monto: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-right font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Descripción</label>
                      <input
                        type="text"
                        value={movimientoManual.descripcion}
                        onChange={(e) => setMovimientoManual((prev) => ({ ...prev, descripcion: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ej: ajuste de fondo, retiro operativo, comisión"
                      />
                    </div>
                    <button
                      onClick={handleRegistrarMovimientoManual}
                      disabled={guardandoMovimientoManual}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                      {guardandoMovimientoManual ? 'Guardando...' : 'Registrar'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Tabla de movimientos */}
              <MovimientosCajaTable
                movimientos={movimientosResumen}
                fecha={fecha}
                onVerDetalle={async (f) => {
                  setDetalleVentasFecha(f);
                  setShowDetalleVentas(true);
                  setLoadingDetalle(true);
                  try {
                    const items = await getDetalleVentasFecha(f);
                    setDetalleVentasItems(items);
                  } catch {
                    setDetalleVentasItems([]);
                  } finally {
                    setLoadingDetalle(false);
                  }
                }}
              />
            </div>
          )}

          {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ Tab CIERRE â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          {activeTab === 'cierre' && (
            <div className="space-y-4">

              {/* â”€â”€â”€ BLOQUE INGRESOS â”€â”€â”€ */}
              <div className="bg-white border border-green-200 rounded-xl shadow-sm overflow-hidden">
                <div className="bg-green-50 px-5 py-3 border-b border-green-200 flex items-center justify-between">
                  <h2 className="text-base font-bold text-green-800 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" /> INGRESOS
                  </h2>
                  <span className="text-lg font-bold font-mono text-green-700">{formatCurrency(totalIngresos)}</span>
                </div>
                <div className="p-5 space-y-4">

                  {/* Ventas por sorteo (Bloque A) */}
                  <div>
                    <button onClick={() => toggleBloque('A')} className="w-full flex items-center justify-between py-1 group">
                      <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 flex items-center gap-2">
                        <ShoppingCart className="w-4 h-4 text-blue-500" /> Ventas de lotería
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-gray-800">{formatCurrency(totalVentasLoteria)}</span>
                        {expandedBloques.A ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </button>
                    {expandedBloques.A && (
                      <div className="mt-2 ml-2 border-l-2 border-blue-200 pl-3">
                        {!resumen?.ventas.sorteos?.length ? (
                          <p className="text-sm text-gray-400 py-2">Sin ventas registradas</p>
                        ) : (
                          <div className="space-y-1">
                            {resumen.ventas.sorteos.map((s) => (
                              <div key={s.sorteo_id} className="flex justify-between text-sm py-1">
                                <span className="text-gray-600">{s.sorteo_nombre}</span>
                                <span className="font-mono text-gray-700">{formatCurrency(s.total_vendido)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <hr className="border-gray-100" />

                  {/* Lotería física (Bloque G) */}
                  <div>
                    <button onClick={() => toggleBloque('G')} className="w-full flex items-center justify-between py-1 group">
                      <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">Lotería física</span>
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-gray-800">{formatCurrency(totalLoteriaFisica)}</span>
                        {expandedBloques.G ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </button>
                    {expandedBloques.G && (
                      <div className="mt-2 ml-2 border-l-2 border-indigo-200 pl-3 space-y-3">
                        {!loteriasAsignadas.length ? (
                          <p className="text-sm text-gray-400 py-2">Sin inventario de lotería física asignado</p>
                        ) : (
                          <>
                            {loteriasAsignadas.map((la) => {
                              const cantFinal = loteriaCantidadFinal[la.juego_id] ?? 0;
                              const vendidos = Math.max(la.neto - cantFinal, 0);
                              return (
                                <div key={la.juego_id} className="flex items-center gap-3 text-sm">
                                  <span className="flex-1 text-gray-700 font-medium">{la.nombre}</span>
                                  <span className="text-xs text-gray-400">Entregado: {la.neto}</span>
                                  <div className="flex flex-col items-end">
                                    <label className="text-xs text-gray-400">Sobrante</label>
                                    <input
                                      type="number"
                                      min={0}
                                      max={la.neto}
                                      value={cantFinal}
                                      onChange={(e) => setLoteriaCantidadFinal(prev => ({ ...prev, [la.juego_id]: parseInt(e.target.value) || 0 }))}
                                      className="w-20 border border-gray-300 rounded px-2 py-1 text-right text-sm font-mono focus:ring-2 focus:ring-indigo-300"
                                    />
                                  </div>
                                  <div className="text-right min-w-[80px]">
                                    <span className="text-xs text-gray-400">Vendidos: </span>
                                    <span className="font-mono text-gray-800 text-sm">{vendidos}</span>
                                    <br />
                                    <span className="font-mono text-green-700 text-sm">{formatCurrency(vendidos * la.valor_unitario)}</span>
                                  </div>
                                </div>
                              );
                            })}
                            {/* Devolución al local */}
                            <div className="mt-3 pt-3 border-t border-indigo-100">
                              <p className="text-xs font-medium text-gray-600 mb-2">Registrar devolución al local</p>
                              <div className="flex gap-2">
                                <select
                                  value={devJuegoId ?? ''}
                                  onChange={(e) => setDevJuegoId(parseInt(e.target.value) || null)}
                                  className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-xs bg-white"
                                >
                                  <option value="">Seleccionar juego...</option>
                                  {loteriasAsignadas.map(la => (
                                    <option key={la.juego_id} value={la.juego_id}>{la.nombre}</option>
                                  ))}
                                </select>
                                <input
                                  type="number"
                                  min="0"
                                  value={devCantidad}
                                  onChange={(e) => setDevCantidad(e.target.value)}
                                  placeholder="Cant."
                                  className="w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-xs text-right"
                                />
                                <button
                                  onClick={handleDevolucion}
                                  disabled={!devJuegoId || !devCantidad}
                                  className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white text-xs rounded-lg font-semibold"
                                >
                                  Devolver
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <hr className="border-gray-100" />

                  {/* Ingresos extra (Bloque F) */}
                  <div>
                    <button onClick={() => toggleBloque('F')} className="w-full flex items-center justify-between py-1 group">
                      <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">Ingresos extra</span>
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-gray-800">{formatCurrency(totalIngresosExtra)}</span>
                        {expandedBloques.F ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </button>
                    {expandedBloques.F && (
                      <div className="mt-2 ml-2 border-l-2 border-green-200 pl-3 space-y-2">
                        {ingresosExtra.map((ing, i) => (
                          <div key={i} className="flex gap-2 items-start">
                            <div className="flex-1">
                              <input type="text" value={ing.concepto} onChange={(e) => updateIngresoExtra(i, 'concepto', e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-400"
                                placeholder="Concepto" />
                            </div>
                            <div className="w-32 shrink-0">
                              <input type="number" inputMode="decimal" value={ing.monto} onChange={(e) => updateIngresoExtra(i, 'monto', e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-right font-mono text-sm focus:ring-2 focus:ring-green-400"
                                placeholder="Monto" />
                            </div>
                            <button onClick={() => removeIngresoExtra(i)} className="text-red-500 hover:text-red-700 p-2">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <button onClick={addIngresoExtra} className="flex items-center gap-1 text-sm text-green-600 hover:text-green-800 font-medium">
                          <Plus className="w-4 h-4" /> Agregar ingreso extra
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="bg-green-100 rounded-lg px-4 py-3 flex items-center justify-between mt-2">
                    <span className="font-bold text-green-800">TOTAL INGRESOS</span>
                    <span className="text-xl font-bold font-mono text-green-800">{formatCurrency(totalIngresos)}</span>
                  </div>
                </div>
              </div>

              {/* â”€â”€â”€ BLOQUE EGRESOS â”€â”€â”€ */}
              <div className="bg-white border border-red-200 rounded-xl shadow-sm overflow-hidden">
                <div className="bg-red-50 px-5 py-3 border-b border-red-200 flex items-center justify-between">
                  <h2 className="text-base font-bold text-red-800 flex items-center gap-2">
                    <TrendingDown className="w-5 h-5" /> EGRESOS
                  </h2>
                  <span className="text-lg font-bold font-mono text-red-700">{formatCurrency(totalEgresos)}</span>
                </div>
                <div className="p-5 space-y-4">

                  {/* Premios (Bloque D) */}
                  <div>
                    <button onClick={() => toggleBloque('D')} className="w-full flex items-center justify-between py-1 group">
                      <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">Premios pagados</span>
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-gray-800">{formatCurrency(totalPremios)}</span>
                        {expandedBloques.D ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </button>
                    {expandedBloques.D && (
                      <div className="mt-2 ml-2 border-l-2 border-red-200 pl-3">
                        {!resumen?.premios.detalle.length ? (
                          <p className="text-sm text-gray-400 py-2">Sin premios pagados</p>
                        ) : (
                          <div className="space-y-1">
                            {resumen.premios.detalle.map((p, i) => (
                              <div key={i} className="flex justify-between text-sm py-1">
                                <span className="text-gray-600 font-mono">{p.ticket_number || p.ticket_id}</span>
                                <span className="font-mono text-gray-700">{formatCurrency(p.monto)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <hr className="border-gray-100" />

                  {/* Vales (Bloque C) */}
                  <div>
                    <button onClick={() => toggleBloque('C')} className="w-full flex items-center justify-between py-1 group">
                      <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">Vales de dinero</span>
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-gray-800">{formatCurrency(totalVales)}</span>
                        {expandedBloques.C ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </button>
                    {expandedBloques.C && (
                      <div className="mt-2 ml-2 border-l-2 border-red-200 pl-3 space-y-2">
                        {vales.map((vale, i) => (
                          <div key={i} className="flex gap-2 items-start">
                            <div className="w-32 shrink-0">
                              <input type="number" inputMode="decimal" value={vale.monto} onChange={(e) => updateVale(i, 'monto', e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-right font-mono text-sm focus:ring-2 focus:ring-red-400"
                                placeholder="Monto" />
                            </div>
                            <div className="flex-1">
                              <input type="text" value={vale.justificacion} onChange={(e) => updateVale(i, 'justificacion', e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-400"
                                placeholder="Justificación (obligatoria)" />
                            </div>
                            <button onClick={() => removeVale(i)} className="text-red-500 hover:text-red-700 p-2">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <button onClick={addVale} className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800 font-medium">
                          <Plus className="w-4 h-4" /> Agregar vale
                        </button>
                      </div>
                    )}
                  </div>

                  <hr className="border-gray-100" />

                  <div className="flex items-center justify-between py-1">
                    <span className="text-sm font-medium text-gray-700">Compras recarga</span>
                    <span className="font-mono font-bold text-gray-800">{formatCurrency(totalComprasRecarga)}</span>
                  </div>

                  <div className="bg-red-100 rounded-lg px-4 py-3 flex items-center justify-between mt-2">
                    <span className="font-bold text-red-800">TOTAL EGRESOS</span>
                    <span className="text-xl font-bold font-mono text-red-800">{formatCurrency(totalEgresos)}</span>
                  </div>
                </div>
              </div>

              {/* Saldo teórico + pagos digitales */}
              <div className="bg-blue-100 border border-blue-300 rounded-xl p-6 shadow-sm">
                <div className="text-center mb-4">
                  <p className="text-sm font-semibold text-blue-700 uppercase tracking-wide mb-1">Saldo teórico</p>
                  <p className="text-3xl font-bold font-mono text-blue-900">{formatCurrency(saldoTeorico)}</p>
                  <p className="text-xs text-blue-500 mt-1">Ingresos {formatCurrency(totalIngresos)} - Egresos {formatCurrency(totalEgresos)}</p>
                </div>
                <div className="bg-white/60 rounded-lg p-4 mt-3 border border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Smartphone className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-bold text-indigo-700">Pagos digitales (no son efectivo)</span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">SINPE Móvil</span>
                      <span className="font-mono font-bold text-indigo-700">{formatCurrency(totalSinpe)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Transferencia</span>
                      <span className="font-mono font-bold text-indigo-700">{formatCurrency(totalTransferencia)}</span>
                    </div>
                    <div className="flex justify-between border-t border-blue-200 pt-1 mt-1">
                      <span className="font-medium text-gray-700">Total digital</span>
                      <span className="font-mono font-bold text-indigo-800">{formatCurrency(totalDigital)}</span>
                    </div>
                  </div>
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <p className="text-xs text-amber-700">
                      <strong>Efectivo esperado en caja:</strong>{' '}
                      <span className="font-mono">{formatCurrency(saldoTeorico)} - {formatCurrency(totalDigital)} = </span>
                      <span className="font-mono font-bold text-amber-900">{formatCurrency(efectivoEsperado)}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Contraste efectivo fisico */}
              <div className={`bg-white border-2 rounded-xl shadow-sm p-5 ${
                !efectivoContado ? 'border-gray-200' :
                estadoDiferencia === 'CUADRA' ? 'border-green-400' :
                estadoDiferencia === 'SOBRA' ? 'border-yellow-400' :
                'border-red-400'
              }`}>
                <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Banknote className="w-5 h-5 text-gray-600" /> Contraste con efectivo fisico
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Efectivo contado</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={efectivoContado}
                      onChange={(e) => setEfectivoContado(e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-xl px-4 h-14 text-right font-mono text-xl font-bold focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0"
                    />
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 flex flex-col justify-center">
                    <span className="text-xs text-blue-500 font-medium">Efectivo esperado</span>
                    <p className="text-xl font-bold font-mono text-blue-700 text-right">{formatCurrency(efectivoEsperado)}</p>
                    <span className="text-[10px] text-blue-400 mt-0.5 text-right">
                      Fondo {formatCurrency(saldoInicialSesion)} + Saldo {formatCurrency(saldoTeorico)} - Digital {formatCurrency(totalDigital)}
                    </span>
                  </div>
                  <div className={`rounded-xl p-4 flex flex-col justify-center ${
                    !efectivoContado ? 'bg-gray-50' :
                    estadoDiferencia === 'CUADRA' ? 'bg-green-50' :
                    estadoDiferencia === 'SOBRA' ? 'bg-yellow-50' :
                    'bg-red-50'
                  }`}>
                    <span className={`text-xs font-medium ${
                      !efectivoContado ? 'text-gray-400' :
                      estadoDiferencia === 'CUADRA' ? 'text-green-600' :
                      estadoDiferencia === 'SOBRA' ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>Diferencia</span>
                    <div className="flex items-center gap-2">
                      {efectivoContado && (
                        estadoDiferencia === 'CUADRA' ? <CheckCircle className="w-5 h-5 text-green-600" /> :
                        estadoDiferencia === 'SOBRA' ? <AlertTriangle className="w-5 h-5 text-yellow-600" /> :
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      <p className={`text-xl font-bold font-mono ${
                        !efectivoContado ? 'text-gray-400' :
                        estadoDiferencia === 'CUADRA' ? 'text-green-700' :
                        estadoDiferencia === 'SOBRA' ? 'text-yellow-700' :
                        'text-red-700'
                      }`}>{efectivoContado ? formatCurrency(diferencia) : '-'}</p>
                    </div>
                    {efectivoContado && (
                      <span className={`text-xs mt-1 font-semibold ${
                        estadoDiferencia === 'CUADRA' ? 'text-green-600' :
                        estadoDiferencia === 'SOBRA' ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {estadoDiferencia === 'CUADRA' ? 'Cuadra exacto' :
                         estadoDiferencia === 'SOBRA' ? 'Sobra dinero' :
                         'Falta dinero'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notas (opcional)</label>
                  <textarea
                    value={notas}
                    onChange={(e) => setNotas(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Observaciones del cierre..."
                  />
                </div>
              </div>

              {/* Créditos informativo (Bloque E) */}
              <div className="bg-white border border-purple-200 rounded-xl shadow-sm overflow-hidden">
                <button onClick={() => toggleBloque('E')} className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50">
                  <h3 className="text-sm font-bold text-purple-700 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" /> Créditos del día (informativo)
                  </h3>
                  <div className="flex items-center gap-3">
                    <span className="font-mono font-bold text-purple-700">{formatCurrency(totalCreditoVendido)}</span>
                    {expandedBloques.E ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </button>
                {expandedBloques.E && (
                  <div className="px-5 pb-4 border-t border-purple-100 pt-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <p className="text-xs text-purple-500 font-medium">Vendido por crédito</p>
                        <p className="text-xl font-bold font-mono text-purple-700">{formatCurrency(totalCreditoVendido)}</p>
                        <p className="text-xs text-purple-400 mt-1">No entra como efectivo</p>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="text-xs text-gray-500 font-medium">Créditos cobrados hoy</p>
                        <p className="text-xl font-bold font-mono text-gray-700">{formatCurrency(resumen?.creditos.total_credito_cobrado || '0')}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* â”€â”€â”€ BotÃ³n cerrar caja â”€â”€â”€ */}
              <div>
                {validationErrors.length > 0 && (
                  <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                    <p className="text-sm font-medium text-yellow-800 mb-1">Pendientes para cerrar:</p>
                    <ul className="text-sm text-yellow-700 list-disc list-inside">
                      {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </div>
                )}
                <button
                  onClick={() => setShowConfirmModal(true)}
                  disabled={!canClose || saving}
                  className={`w-full h-14 text-lg font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors ${
                    !canClose || saving
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg'
                  }`}
                >
                  {saving
                    ? <><Loader2 className="w-5 h-5 animate-spin" /> Cerrando...</>
                    : <><Lock className="w-5 h-5" /> Cerrar caja del día</>
                  }
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* â”€â”€â”€ Modal: detalle de ventas â”€â”€â”€ */}
      {showDetalleVentas && (
        <Modal
          isOpen={showDetalleVentas}
          onClose={() => setShowDetalleVentas(false)}
          title={`Detalle de ventas - ${detalleVentasFecha ?? ''}`}
        >
          <div className="space-y-3">
            {loadingDetalle ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                <span className="ml-2 text-gray-500 text-sm">Cargando...</span>
              </div>
            ) : detalleVentasItems.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">Sin ventas registradas para esta fecha.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Tipo</th>
                      <th className="px-3 py-2 text-left font-semibold">Descripción</th>
                      <th className="px-3 py-2 text-right font-semibold">Monto</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {detalleVentasItems.map((mov) => (
                      <tr key={mov.id}>
                        <td className="px-3 py-2">
                          <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700">
                            {mov.tipo === 'VENTA_CREDITO' ? 'Crédito' : 'Venta'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-700">{mov.descripcion}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700">
                          +{formatCurrency(mov.monto)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={2} className="px-3 py-2 text-sm font-semibold text-gray-700">Total</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">
                        +{formatCurrency(detalleVentasItems.reduce((s, m) => s + parseFloat(m.monto), 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Modal: confirmación de cierre */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirmar cierre de caja"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            ¿Está seguro de cerrar la caja del día <strong>{fecha}</strong>?
          </p>
          <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
            <div className="flex justify-between">
              <span>Total ingresos:</span>
              <span className="font-mono font-bold text-green-700">{formatCurrency(totalIngresos)}</span>
            </div>
            <div className="flex justify-between">
              <span>Total egresos:</span>
              <span className="font-mono font-bold text-red-700">{formatCurrency(totalEgresos)}</span>
            </div>
            <div className="flex justify-between border-t pt-1">
              <span>Saldo teórico:</span>
              <span className="font-mono font-bold text-blue-700">{formatCurrency(saldoTeorico)}</span>
            </div>
            {totalDigital > 0 && (
              <div className="flex justify-between text-indigo-600">
                <span>Pagos digitales:</span>
                <span className="font-mono font-bold">-{formatCurrency(totalDigital)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>Efectivo esperado:</span>
              <span className="font-mono font-bold text-blue-700">{formatCurrency(efectivoEsperado)}</span>
            </div>
            <div className="flex justify-between">
              <span>Efectivo contado:</span>
              <span className="font-mono font-bold">{formatCurrency(efectivoNum)}</span>
            </div>
            <div className={`flex justify-between border-t pt-1 font-bold ${
              estadoDiferencia === 'CUADRA' ? 'text-green-700' :
              estadoDiferencia === 'SOBRA' ? 'text-yellow-700' :
              'text-red-700'
            }`}>
              <span>Diferencia:</span>
              <span className="font-mono">{formatCurrency(diferencia)} ({estadoDiferencia})</span>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Una vez cerrada, solo un usuario con permisos superiores (LOCAL/BANCA) puede anular el cierre.
          </p>
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>Cancelar</Button>
            <Button variant="primary" onClick={handleCerrar}>Confirmar cierre</Button>
          </div>
        </div>
      </Modal>

      {/* Modal: advertencia de apertura múltiple */}
      {showAdvertenciaApertura && (
        <Modal
          isOpen={showAdvertenciaApertura}
          onClose={() => { setShowAdvertenciaApertura(false); setPendienteConfirmarApertura(false); }}
          title="Advertencia: Apertura múltiple de caja"
        >
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex gap-2">
              <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-yellow-900">{advertenciaMsg}</p>
                <p className="text-yellow-800 text-sm mt-1">Está a punto de abrir la caja nuevamente en el mismo día operativo.</p>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-blue-800 text-sm">
                <strong>Recordatorio:</strong> Las aperturas múltiples son permitidas para soporte operativo continuo, pero están auditadas y limitadas a 3 por día.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={() => { setShowAdvertenciaApertura(false); setPendienteConfirmarApertura(false); }}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleConfirmarAperturaAdvertida}>
                Confirmar apertura
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CajaVendedorPage;
